<script lang="ts" setup>
import { formatPerformanceComposite } from '#leaderboards/utils/performance'
import { resolveMmLogo } from '#public-research/utils/pr.utils'
import { formatValue } from '@forgd/formatter'
import { Avatar, AvatarFallback, AvatarImage } from '@forgd/ui-kit/avatar'
import { Badge } from '@forgd/ui-kit/badge'
import { Button } from '@forgd/ui-kit/button'
import { Card, CardHeader, CardTitle } from '@forgd/ui-kit/card'
import { Skeleton } from '@forgd/ui-kit/skeleton'
import { ArrowRightIcon } from '@heroicons/vue/24/outline'

const TOP_COUNT = 3
const PLACEHOLDER_LOGO = resolveMmLogo(null)

const { performance, isLoading } = useLeaderboardPodiumData()

const stats = computed(() => {
  const mms = performance.value ?? []
  const { totalEngagements, combinedFdv } = mms.reduce(
    (acc, mm) => {
      const count = mm.activeEngagementsCount ?? 0
      acc.totalEngagements += count
      acc.combinedFdv += count * (mm.medianEngagementsFdv ?? 0)
      return acc
    },
    { totalEngagements: 0, combinedFdv: 0 },
  )
  return [
    { label: 'Integrated MMs', value: mms.length ? formatValue(mms.length, 'number:0') : null },
    { label: 'Engagements Tracked', value: mms.length ? formatValue(totalEngagements, 'number:0') : null },
    { label: 'Comb. Tracked FDV', value: combinedFdv > 0 ? formatValue(combinedFdv, 'currency:short,2') : null },
  ]
})

const topThree = computed(() => {
  const items = (performance.value ?? []).slice(0, TOP_COUNT).map((mm, i) => ({
    name: mm.marketMakerName,
    logoSrc: resolveMmLogo(mm.marketMakerImageUrl),
    composite: formatPerformanceComposite(mm.composite ?? null),
    rank: i + 1,
  }))
  while (items.length < TOP_COUNT) {
    items.push({ name: '', logoSrc: PLACEHOLDER_LOGO, composite: '', rank: 0 })
  }
  return items
})
</script>

<template>
  <Card class="flex h-full flex-col">
    <CardHeader class="flex flex-row items-center justify-between border-b border-default p-3 sm:pl-5">
      <div class="flex items-center gap-2">
        <CardTitle>
          Market Maker Leaderboard
        </CardTitle>
        <Badge variant="gray">
          30D
        </Badge>
      </div>
      <Button
        as-child
        variant="outline"
        size="sm"
        icon-only
      >
        <NuxtLink to="/market-maker-leaderboard" aria-label="Open Market Maker Leaderboard">
          <ArrowRightIcon />
        </NuxtLink>
      </Button>
    </CardHeader>

    <div class="grid grid-cols-1 divide-y divide-default border-b border-default sm:grid-cols-3 sm:divide-x sm:divide-y-0">
      <div
        v-for="stat in stats"
        :key="stat.label"
        class="flex items-center justify-between gap-2 p-3 sm:flex-col sm:items-start sm:p-4"
      >
        <span class="text-xs text-secondary">{{ stat.label }}</span>
        <Skeleton v-if="isLoading" class="h-4 w-20" />
        <span v-else class="text-sm leading-none font-semibold text-primal sm:text-md">{{ stat.value ?? 'N/A' }}</span>
      </div>
    </div>

    <div class="hidden flex-1 grid-cols-3 divide-x divide-default sm:grid" aria-hidden="true">
      <div />
      <div />
      <div />
    </div>

    <div class="grid grid-cols-1 divide-y divide-default sm:grid-cols-3 sm:divide-x sm:divide-y-0">
      <div
        v-for="(mm, i) in topThree"
        :key="mm.name || `placeholder-${i}`"
        class="flex items-center justify-between gap-2 p-3 sm:flex-col sm:items-start sm:p-4"
      >
        <div class="flex min-w-0 items-center gap-2">
          <template v-if="isLoading">
            <Skeleton class="size-5 shrink-0 rounded-full" />
            <Skeleton class="h-5 w-28" />
          </template>
          <template v-else>
            <Avatar class="size-5 shrink-0 border border-default bg-white">
              <AvatarImage :src="mm.logoSrc" :alt="mm.name" />
              <AvatarFallback class="text-2xs">
                {{ mm.name.slice(0, 1) }}
              </AvatarFallback>
            </Avatar>
            <span class="truncate text-sm leading-none font-semibold text-primal sm:text-md">{{ mm.name }}</span>
          </template>
        </div>
        <div class="flex shrink-0 items-center gap-1 text-2xs leading-none whitespace-nowrap sm:w-full sm:justify-between">
          <div class="flex items-center gap-1 text-secondary">
            <span>Composite Score:</span>
            <Skeleton v-if="isLoading" class="h-2.5 w-10" />
            <span v-else class="font-semibold text-primal">{{ mm.composite }}</span>
          </div>
          <span v-if="mm.rank" class="font-mono font-medium text-placeholder">Rank: {{ mm.rank }}</span>
        </div>
      </div>
    </div>
  </Card>
</template>
